import { Workbox } from "workbox-window";
import {
  startAppUpdate,
  type AppUpdateSession,
  type UpdateWorker,
} from "./appUpdate";
import { hasUnsavedWork } from "./unsavedWork";

const workerOptions: RegistrationOptions = {
  type: "classic",
  updateViaCache: "none",
};

const listeners = new Set<() => void>();
const dismissers = new Set<() => void>();
let session: AppUpdateSession | undefined;

function productionWorker(): UpdateWorker {
  const wb = new Workbox(`${import.meta.env.BASE_URL}sw.js`, workerOptions);
  return {
    addEventListener(type, listener) {
      if (type === "waiting") {
        wb.addEventListener("waiting", (event) => {
          listener({
            wasWaitingBeforeRegister: event.wasWaitingBeforeRegister,
            worker: event.sw,
          });
        });
        return;
      }
      wb.addEventListener("controlling", (event) => {
        listener({ isUpdate: event.isUpdate, worker: event.sw });
      });
    },
    messageSkipWaiting() {
      wb.messageSkipWaiting();
    },
    register(options) {
      return wb.register(options);
    },
  };
}

/**
 * Registers the production service worker once. Dev has no generated worker,
 * so a local session never reloads itself.
 *
 * `reloadedForUpdate` is the page Vernieuwen just opened. It skips the worker
 * that was already waiting, not a deploy that arrives later.
 */
export function registerProductionUpdate(
  onNeedRefresh: () => void,
  reloadedForUpdate = false,
  onDismiss?: () => void,
): AppUpdateSession {
  listeners.add(onNeedRefresh);
  if (onDismiss) dismissers.add(onDismiss);

  if (!session) {
    if (import.meta.env.DEV || !("serviceWorker" in navigator)) {
      session = { acceptUpdate() {} };
    } else {
      session = startAppUpdate({
        createWorker: productionWorker,
        reload: () => window.location.reload(),
        storage: sessionStorage,
        onNeedRefresh: () => {
          listeners.forEach((listener) => listener());
        },
        onDismiss: () => {
          dismissers.forEach((listener) => listener());
        },
        suppressed: reloadedForUpdate,
        watch: true,
        hasUnsavedState: () => hasUnsavedWork(),
      });
    }
  }

  return {
    acceptUpdate: () => session?.acceptUpdate(),
  };
}
